import React, { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
    reverseGeocode,
    fetchCurrentAndForecast,
    fetchPast,
    searchCitiesInBBox,
    fetchCitiesWeatherInBBoxOpenWeather,
    getLocationSales
} from "../../services/weather";
import { WiThermometer, WiHumidity, WiRaindrop, WiStrongWind, WiBarometer, WiDaySunny, WiCloudy, WiTime3 } from "react-icons/wi";
import { MdLocationOn, MdAccessTime, MdMenu, MdClose, MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { FiClock } from "react-icons/fi";
import { HiViewGrid, HiViewList } from "react-icons/hi";
import LeftPanel from "./components/LeftPanel";
import ClockBar from "./components/ClockBar";
import LocationButton from "./components/LocationButton";

const DEFAULT_CENTER = [22.9734, 78.6569]; // India centroid approx
const MIN_ZOOM = 3; // Country level - one country at a time
const MAX_ZOOM = 10; // City level - no street details

const glass = "backdrop-blur-xl bg-gradient-to-br from-white/20 to-white/5 border border-white/30 shadow-2xl";

function formatDate(date) {
    const d = new Date(date);
    return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
}

function formatTime(date, use24h) {
    const d = new Date(date);
    return d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: !use24h,
    });
}

function getLastNDates(n) {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (n - 1));
    const toYMD = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
            d.getDate()
        ).padStart(2, "0")}`;
    return { start: toYMD(start), end: toYMD(end) };
}

const Weather = () => {
    const [center, setCenter] = useState(DEFAULT_CENTER); // [lat, lon]
    const [zoom, setZoom] = useState(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, 5))); // Clamp initial zoom
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const cityMarkersRef = useRef({}); // id -> maplibre Marker
    const cityWeatherCacheRef = useRef(new Map()); // key: "lat,lon" -> { code, ts }
    const debounceTimerRef = useRef(null);

    const [place, setPlace] = useState(null);
    const [selectedLabel, setSelectedLabel] = useState(null);

    const [tab, setTab] = useState("current"); // current | forecast | past
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [currentData, setCurrentData] = useState(null);
    const [forecastData, setForecastData] = useState(null);
    const [pastData, setPastData] = useState(null);

    const [use24h, setUse24h] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [showLeftPanel, setShowLeftPanel] = useState(true);
    const [showRightPanel, setShowRightPanel] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLocating, setIsLocating] = useState(false);
    const [cityLoading, setCityLoading] = useState(false);
    const [cityError, setCityError] = useState("");
    const [locationSalesData, setLocationSalesData] = useState(null);
    const [locationSalesLoading, setLocationSalesLoading] = useState(false);
    const [expandedMonths, setExpandedMonths] = useState({});
    const citiesRequestIdRef = useRef(0);
    const inFlightKeysRef = useRef(new Set());

    // Initialize MapLibre map
    useEffect(() => {
        if (mapInstanceRef.current || !mapRef.current) return;
        // Satellite base + transparent labels overlay for place names
        const satelliteStyle = {
            version: 8,
            sources: {
                satellite: {
                    type: "raster",
                    tiles: [
                        "https://api.maptiler.com/maps/satellite/256/{z}/{x}/{y}.jpg?key=uFt2BoWi8PwTxvc35csD"
                    ],
                    tileSize: 256,
                    attribution:
                        "© Rashail"
                },
                labels: {
                    type: "raster",
                    tiles: [
                        "https://a.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}.png",
                        "https://b.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}.png",
                        "https://c.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}.png",
                        "https://d.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}.png"
                    ],
                    tileSize: 256,
                    attribution: "© Rashail"
                }
            },
            layers: [
                {
                    id: "satellite-layer",
                    type: "raster",
                    source: "satellite",
                    minzoom: 0,
                    maxzoom: 22
                },
                {
                    id: "labels-layer",
                    type: "raster",
                    source: "labels",
                    minzoom: 0,
                    maxzoom: 22,
                    paint: {
                        "raster-opacity": 1.0
                    }
                }
            ]
        };
        const map = new maplibregl.Map({
            container: mapRef.current,
            style: satelliteStyle,
            center: [center[1], center[0]], // [lon, lat]
            zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom)), // Clamp zoom to limits
            minZoom: MIN_ZOOM,
            maxZoom: MAX_ZOOM,
            pitch: 0,
            attributionControl: true,
        });
        map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-right");
        map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: "metric" }));

        map.on("click", (e) => {
            const lat = e.lngLat.lat;
            const lon = e.lngLat.lng;
            handleMapSelect(lat, lon, { keepView: true });
        });

        const scheduleUpdateVisibleCities = () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
                updateVisibleCities();
            }, 300);
        };

        map.on("moveend", scheduleUpdateVisibleCities);
        map.on("zoomend", scheduleUpdateVisibleCities);
        map.on("load", scheduleUpdateVisibleCities);
        map.on("idle", scheduleUpdateVisibleCities);

        mapInstanceRef.current = map;
        // Initial marker
        markerRef.current = new maplibregl.Marker({ color: "#22c55e" })
            .setLngLat([center[1], center[0]])
            .addTo(map);
        // Trigger first city icons render immediately
        scheduleUpdateVisibleCities();

        return () => {
            // Cleanup markers and listeners on unmount
            try {
                map.off("moveend", scheduleUpdateVisibleCities);
                map.off("zoomend", scheduleUpdateVisibleCities);
                map.off("load", scheduleUpdateVisibleCities);
            } catch { }
            Object.values(cityMarkersRef.current || {}).forEach((m) => {
                try { m.remove(); } catch { }
            });
            cityMarkersRef.current = {};
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const locationTitle = useMemo(() => {
        if (!place?.address) return "Select a location";
        const a = place.address;
        const city = a.city || a.town || a.village || a.hamlet || a.suburb;
        const state = a.state || a.region;
        const country = a.country;
        if (selectedLabel) return selectedLabel;
        if (city && state) return `${city}, ${state}`;
        if (state && country) return `${state}, ${country}`;
        return place.display_name?.split(",").slice(0, 2).join(", ") || "Location";
    }, [place, selectedLabel]);

    async function loadAll(lat, lon) {
        setLoading(true);
        setError("");
        try {
            const [rg, cf] = await Promise.all([
                reverseGeocode(lat, lon),
                fetchCurrentAndForecast(lat, lon),
            ]);
            setPlace(rg);
            setCurrentData(cf.current || null);
            setForecastData(cf);

            // Get location name for sales API call
            if (rg?.address) {
                const a = rg.address;
                const locationName = a.city || a.town || a.village || a.hamlet || a.suburb || a.state || a.region || rg.display_name?.split(",")[0] || "Unknown";
                // Call getLocationSales with location name
                setLocationSalesLoading(true);
                setLocationSalesData(null);
                getLocationSales(locationName)
                    .then((data) => {
                        setLocationSalesData(data);
                        setLocationSalesLoading(false);
                    })
                    .catch((err) => {
                        console.error("Failed to fetch location sales:", err);
                        setLocationSalesData(null);
                        setLocationSalesLoading(false);
                    });
            }

            // fetch past separately (non-blocking)
            const { start, end } = getLastNDates(7);
            fetchPast(lat, lon, start, end)
                .then(setPastData)
                .catch(() => setPastData(null));
        } catch (e) {
            setError("Unable to load weather data for this location.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        // initial load: try to use current location first, fallback to default center
        let resolved = false;
        const fallback = () => {
            if (resolved) return;
            resolved = true;
            loadAll(center[0], center[1]);
        };
        try {
            if (navigator.geolocation) {
                setIsLocating(true);
                const success = (position) => {
                    if (resolved) return;
                    resolved = true;
                    setIsLocating(false);
                    const lat = position.coords.latitude;
                    const lon = position.coords.longitude;
                    setCenter([lat, lon]);
                    setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, 10)));
                    setSelectedLabel(null);
                    loadAll(lat, lon);
                };
                const error = () => {
                    setIsLocating(false);
                    fallback();
                };
                navigator.geolocation.getCurrentPosition(success, error, {
                    enableHighAccuracy: true,
                    timeout: 7000,
                    maximumAge: 0,
                });
                // Ensure we fallback if geolocation hangs
                setTimeout(fallback, 8000);
            } else {
                fallback();
            }
        } catch {
            fallback();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update current time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // update map view on center/zoom
    useEffect(() => {
        if (!mapInstanceRef.current) return;
        const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
        mapInstanceRef.current.jumpTo({ center: [center[1], center[0]], zoom: clampedZoom });
        if (markerRef.current) {
            markerRef.current.setLngLat([center[1], center[0]]);
        }
    }, [center, zoom]);

    const pulseMarkerRef = useRef(null);
    const ensurePulseStyles = () => {
        if (document.getElementById("pulse-marker-styles")) return;
        const style = document.createElement("style");
        style.id = "pulse-marker-styles";
        style.textContent = `
@keyframes pulseScale {
  0% { transform: scale(0.8); opacity: 0.85; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(0.8); opacity: 0.85; }
}`;
        document.head.appendChild(style);
    };

    const handleMapSelect = (lat, lon, opts = {}) => {
        const keepView = opts.keepView === true;
        // Do not change map view or zoom if keepView is requested
        if (!keepView) {
            setCenter([lat, lon]);
            setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, 8))); // Clamp zoom
        }
        setSelectedLabel(null);
        loadAll(lat, lon);

        // Add/update a small pulsing marker at clicked location
        ensurePulseStyles();
        const map = mapInstanceRef.current;
        if (map) {
            const container = document.createElement("div");
            container.style.position = "relative";
            container.style.width = "0px";
            container.style.height = "0px";
            const dot = document.createElement("span");
            dot.style.position = "absolute";
            dot.style.left = "-7px";
            dot.style.top = "-7px";
            dot.style.width = "14px";
            dot.style.height = "14px";
            dot.style.borderRadius = "50%";
            dot.style.background = "#22c55e";
            dot.style.boxShadow = "0 0 0 6px rgba(34,197,94,0.25)";
            dot.style.animation = "pulseScale 1.6s ease-in-out infinite";
            container.appendChild(dot);
            const mk = new maplibregl.Marker({ element: container, anchor: "center" })
                .setLngLat([lon, lat])
                .addTo(map);
            if (pulseMarkerRef.current) {
                try { pulseMarkerRef.current.remove(); } catch { }
            }
            pulseMarkerRef.current = mk;
        }
    };

    // Map Open-Meteo weather_code OR OpenWeather conditionId to an emoji icon for lightweight markers
    const getEmojiForWeatherCode = (code) => {
        if (code == null) return "❓";
        // If code is in Open-Meteo range (0-99), keep existing mapping
        if (code >= 0 && code < 100) {
            if (code === 0) return "☀️"; // Clear
            if ([1, 2, 3].includes(code)) return "⛅"; // Mainly clear / partly cloudy
            if ([45, 48].includes(code)) return "🌫️"; // Fog
            if ([51, 53, 55, 56, 57].includes(code)) return "🌦️"; // Drizzle
            if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return "🌧️"; // Rain
            if ([71, 73, 75, 77, 85, 86].includes(code)) return "🌨️"; // Snow
            if ([95, 96, 97, 99].includes(code)) return "⛈️"; // Thunder
            return "☁️";
        }
        // Otherwise, treat this as an OpenWeather condition id
        if (code >= 200 && code < 300) return "⛈️"; // Thunderstorm
        if (code >= 300 && code < 600) return "🌧️"; // Drizzle/Rain
        if (code >= 600 && code < 700) return "🌨️"; // Snow
        if (code >= 700 && code < 800) return "🌫️"; // Atmosphere (mist, smoke, etc.)
        if (code === 800) return "☀️"; // Clear
        if (code > 800 && code < 900) return "⛅"; // Clouds
        return "☁️";
    };

    // Create or update markers for visible cities based on current viewport
    const updateVisibleCities = async () => {
        const requestId = ++citiesRequestIdRef.current;
        const map = mapInstanceRef.current;
        if (!map) return;
        const bounds = map.getBounds();
        const minLon = bounds.getWest();
        const minLat = bounds.getSouth();
        const maxLon = bounds.getEast();
        const maxLat = bounds.getNorth();
        const z = map.getZoom();

        // Avoid clutter at very low zooms
        if (z < MIN_ZOOM) {
            // Remove all city markers if too zoomed out
            Object.values(cityMarkersRef.current || {}).forEach((m) => {
                try { m.remove(); } catch { }
            });
            cityMarkersRef.current = {};
            setCityLoading(false);
            return;
        }

        setCityError("");
        setCityLoading(true);

        // Limit number of places based on zoom level
        // Increase limits to better match visible label density
        const limit = z < 5 ? 20 : z < 6 ? 28 : z < 7 ? 36 : z < 8 ? 44 : 52;
        console.log(minLon, minLat, maxLon, maxLat, limit);
        let places = [];
        let bulkWeatherUsed = false;
        try {
            // Prefer OpenWeather bulk bbox endpoint when API key is configured
            console.log("🔄 Attempting OpenWeather bulk fetch for bbox...");
            const owPlaces = await fetchCitiesWeatherInBBoxOpenWeather(
                minLon,
                minLat,
                maxLon,
                maxLat,
                z,
                limit
            );
            if (Array.isArray(owPlaces) && owPlaces.length > 0) {
                console.log("✅ OpenWeather bulk fetch succeeded, got", owPlaces.length, "cities");
                places = owPlaces;
                bulkWeatherUsed = true;
            } else {
                console.warn("⚠️ OpenWeather returned empty array, falling back to Overpass");
            }
        } catch (e) {
            // If OpenWeather bulk fails (no key / network / quota), fall back to Overpass + per-city Open-Meteo
            console.error("❌ OpenWeather bulk bbox failed, falling back to Overpass/Nominatim:", e.message || e);
        }

        if (!bulkWeatherUsed) {
            try {
                places = await searchCitiesInBBox(minLon, minLat, maxLon, maxLat, limit);
            } catch (e) {
                // Do not clear existing markers; keep current view intact
                setCityError("Unable to load nearby cities. Please retry.");
                setCityLoading(false);
                return;
            }
            if (!Array.isArray(places) || places.length === 0) {
                // Keep previous markers; do not clear cache
                setCityError("No city data available (rate limited). Try again.");
                setCityLoading(false);
                return;
            }
        }

        const existing = cityMarkersRef.current || {};
        // Remove only markers that fall outside current bounds (keep visible cached markers)
        Object.entries(existing).forEach(([key, mk]) => {
            try {
                const lngLat = mk.getLngLat();
                if (!bounds.contains(lngLat)) {
                    try { mk.remove(); } catch { }
                    delete existing[key];
                }
            } catch { /* ignore */ }
        });

        // Helper function to create marker element
        const createMarkerElement = (emoji, temp) => {
            const el = document.createElement("div");
            el.style.background = "rgba(0,0,0,0.45)";
            el.style.color = "#fff";
            el.style.padding = "4px 8px";
            el.style.border = "1px solid rgba(255,255,255,0.35)";
            el.style.borderRadius = "12px";
            el.style.fontSize = "11px";
            el.style.fontWeight = "700";
            el.style.boxShadow = "0 6px 16px rgba(0,0,0,0.25)";
            el.style.backdropFilter = "blur(6px)";
            el.style.whiteSpace = "nowrap";
            el.style.pointerEvents = "none";
            el.style.zIndex = "1000";
            const tempText = temp != null ? `${Math.round(temp)}°` : "…";
            el.textContent = `${emoji} ${tempText}`;
            return el;
        };

        // Add markers; when bulk OpenWeather was used, we already have temp/code,
        // otherwise we create loading markers and will fetch per-city Open-Meteo later.
        let placesToFetch = [];
        // Stable cache key generator (prefer backend id, fallback to rounded coords)
        const getPlaceCacheKey = (p) => {
            const roundedLat = Number(p.lat).toFixed(4);
            const roundedLon = Number(p.lon).toFixed(4);
            return `${p.id ?? "noid"}|${roundedLat},${roundedLon}`;
        };

        for (const place of places) {
            const id = getPlaceCacheKey(place);
            let mk = existing[id];
            if (!mk) {
                // Create marker immediately
                const initialEmoji = bulkWeatherUsed ? getEmojiForWeatherCode(place.conditionId ?? null) : "⏳";
                const initialTemp = bulkWeatherUsed ? (place.temp ?? null) : null;
                const el = createMarkerElement(initialEmoji, initialTemp);
                mk = new maplibregl.Marker({ element: el, anchor: "bottom" })
                    .setLngLat([place.lon, place.lat])
                    .addTo(map);
                existing[id] = mk;
            }

            const cacheKey = getPlaceCacheKey(place);

            if (bulkWeatherUsed) {
                // Populate cache from bulk result so we don't re-fetch for this location
                const code = place.conditionId ?? null;
                const temp = place.temp ?? null;
                cityWeatherCacheRef.current.set(cacheKey, {
                    code,
                    temp,
                    ts: Date.now(),
                });
            } else {
                // No bulk weather; fall back to per-city Open-Meteo fetches
                const cached = cityWeatherCacheRef.current.get(cacheKey);
                if (cached) {
                    const emoji = getEmojiForWeatherCode(cached.code);
                    const tempText = cached.temp != null ? `${Math.round(cached.temp)}°` : "…";
                    const el = mk.getElement();
                    if (el) el.textContent = `${emoji} ${tempText}`;
                } else if (!inFlightKeysRef.current.has(cacheKey)) {
                    inFlightKeysRef.current.add(cacheKey);
                    placesToFetch.push({ place, id, cacheKey, marker: mk });
                }
            }
        }

        // Thin markers to avoid clutter when zooming out
        const applyThinning = (mapObj, markersObj, zoomLevel) => {
            // Configure max markers and grid cell size by zoom level
            let maxMarkers = Infinity;
            let cellPx = 0;
            if (zoomLevel < 4) {
                maxMarkers = 30;
                cellPx = 160;
            } else if (zoomLevel < 5) {
                maxMarkers = 50;
                cellPx = 120;
            } else if (zoomLevel < 6) {
                maxMarkers = 70;
                cellPx = 100;
            } else if (zoomLevel < 7) {
                maxMarkers = 90;
                cellPx = 80;
            } else {
                return new Set(Object.keys(markersObj));
            }

            const hasData = (marker) => {
                try {
                    const txt = marker.getElement()?.textContent || "";
                    return txt.includes("°"); // temperature present
                } catch { return false; }
            };

            const cellToKeptId = new Map();
            const keptIds = new Set();
            const entries = Object.entries(markersObj);
            for (const [id, mk] of entries) {
                try {
                    const ll = mk.getLngLat();
                    const p = mapObj.project([ll.lng, ll.lat]);
                    const cx = Math.floor(p.x / cellPx);
                    const cy = Math.floor(p.y / cellPx);
                    const key = `${cx},${cy}`;
                    if (!cellToKeptId.has(key)) {
                        cellToKeptId.set(key, id);
                        keptIds.add(id);
                    } else {
                        const existingId = cellToKeptId.get(key);
                        const existingMarker = markersObj[existingId];
                        // Prefer marker with actual data over loading/unknown
                        if (!hasData(existingMarker) && hasData(mk)) {
                            keptIds.delete(existingId);
                            keptIds.add(id);
                            cellToKeptId.set(key, id);
                        }
                    }
                    if (keptIds.size >= maxMarkers) break;
                } catch { /* ignore bad marker */ }
            }
            return keptIds;
        };

        const keptIds = applyThinning(map, existing, z);
        // Remove markers not kept
        Object.entries(existing).forEach(([id, mk]) => {
            if (!keptIds.has(id)) {
                try { mk.remove(); } catch { }
                delete existing[id];
            }
        });
        // Filter pending fetches to only kept markers
        placesToFetch = placesToFetch.filter((item) => keptIds.has(item.id));

        cityMarkersRef.current = existing;

        // If we already used OpenWeather bulk, we don't need per-city fetches.
        // Only run the Open-Meteo fallback when bulkWeatherUsed === false.
        if (!bulkWeatherUsed && placesToFetch.length > 0) {
            // Fetch weather data in parallel batches (8 at a time to avoid rate limits)
            const batchSize = 8;
            for (let i = 0; i < placesToFetch.length; i += batchSize) {
                // If a newer request has started (due to pan/zoom), abort this run
                if (citiesRequestIdRef.current !== requestId) break;
                const batch = placesToFetch.slice(i, i + batchSize);
                const promises = batch.map(async ({ place, id, cacheKey, marker }) => {
                    try {
                        const cf = await fetchCurrentAndForecast(place.lat, place.lon);
                        const code = cf?.current?.weather_code ?? null;
                        const temp = cf?.current?.temperature_2m ?? null;
                        const now = Date.now();
                        cityWeatherCacheRef.current.set(cacheKey, { code, temp, ts: now });

                        // Update marker with weather data
                        const emoji = getEmojiForWeatherCode(code);
                        const tempText = temp != null ? `${Math.round(temp)}°` : "…";
                        const markerEl = marker?.getElement();
                        if (markerEl) {
                            markerEl.textContent = `${emoji} ${tempText}`;
                        }
                    } catch (err) {
                        // On error, show error state
                        const markerEl = marker?.getElement();
                        if (markerEl) {
                            markerEl.textContent = `❓ …`;
                        }
                    } finally {
                        inFlightKeysRef.current.delete(cacheKey);
                    }
                });

                // Wait for batch to complete before starting next batch
                await Promise.allSettled(promises);

                // Small delay between batches to avoid rate limiting
                if (i + batchSize < placesToFetch.length) {
                    await new Promise(resolve => setTimeout(resolve, 200));
                }
            }
        }

        setCityLoading(false);
    };

    const handleGetCurrentLocation = () => {
        if (!navigator.geolocation) {
            setError("Geolocation is not supported by your browser.");
            return;
        }

        setIsLocating(true);
        setError("");

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lon = position.coords.longitude;
                setCenter([lat, lon]);
                setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, 10))); // Clamp zoom to max allowed
                setSelectedLabel(null);
                loadAll(lat, lon);
                setIsLocating(false);
            },
            (error) => {
                setIsLocating(false);
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        setError("Location access denied. Please enable location permissions.");
                        break;
                    case error.POSITION_UNAVAILABLE:
                        setError("Location information unavailable.");
                        break;
                    case error.TIMEOUT:
                        setError("Location request timed out.");
                        break;
                    default:
                        setError("An unknown error occurred while getting location.");
                        break;
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    };

    const addressChips = useMemo(() => {
        if (!place?.address) return [];
        const a = place.address;
        const entries = [
            a.city || a.town || a.village || a.hamlet || a.suburb,
            a.state || a.region,
            a.country,
        ].filter(Boolean);
        return Array.from(new Set(entries));
    }, [place]);

    // Format current date and time
    const formattedDate = useMemo(() => {
        return currentTime.toLocaleDateString(undefined, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }, [currentTime]);

    const formattedTime = useMemo(() => {
        return currentTime.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: !use24h
        });
    }, [currentTime, use24h]);

    return (
        <div className={`${isFullscreen ? 'fixed inset-0' : 'relative'} w-full h-full overflow-hidden`}>
            {/* MapLibre Map */}
            <div ref={mapRef} className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }} />

            {/* Toggle Buttons */}
            <div className="absolute top-1 left-1 z-[10001] flex gap-1">
                {!showLeftPanel && (
                    <button
                        onClick={() => setShowLeftPanel(true)}
                        className={`p-1.5 rounded-lg ${glass} text-white hover:bg-white/20 transition-all duration-200`}
                        title="Show location panel"
                    >
                        <HiViewList className="text-xl" />
                    </button>
                )}
            </div>

            {/* Top-right buttons - only show when weather panel is closed */}
            {!showRightPanel && (
                <div className="absolute top-1 right-1 z-[10001] flex gap-1.5">
                    <button
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        className={`p-1.5 rounded-lg ${glass} text-white hover:bg-white/20 transition-all duration-200 ${isFullscreen ? 'bg-blue-500/30' : ''}`}
                        title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                    >
                        {isFullscreen ? <MdFullscreenExit className="text-xl" /> : <MdFullscreen className="text-xl" />}
                    </button>
                    <button
                        onClick={() => setShowRightPanel(true)}
                        className={`p-1.5 rounded-lg ${glass} text-white hover:bg-white/20 transition-all duration-200`}
                        title="Show weather details"
                    >
                        <HiViewGrid className="text-xl" />
                    </button>
                </div>
            )}


            <LeftPanel
                glass={glass}
                showLeftPanel={showLeftPanel}
                setShowLeftPanel={setShowLeftPanel}
                locationTitle={locationTitle}
                center={center}
                forecastData={forecastData}
                place={place}
                addressChips={addressChips}
                selectedLabel={selectedLabel}
                setSelectedLabel={setSelectedLabel}
                tab={tab}
                setTab={setTab}
            />

            {/* Right-side details panel */}
            {showRightPanel && (
                <div className={`absolute top-1 right-1 p-2 rounded-2xl ${glass} text-white w-[90vw] md:w-[300px] max-h-[calc(100vh-5.5rem)] overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent z-[10002] transition-all duration-300 hover:shadow-3xl`}>
                    <div className="flex items-center justify-between mb-2 gap-2">
                        <div className="text-xs font-bold opacity-80">Weather Data</div>
                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                className={`p-1.5 rounded hover:bg-white/20 transition-all duration-200 ${isFullscreen ? 'bg-blue-500/30' : ''}`}
                                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                            >
                                {isFullscreen ? <MdFullscreenExit className="text-base" /> : <MdFullscreen className="text-base" />}
                            </button>
                            <button
                                onClick={() => setShowRightPanel(false)}
                                className="p-1 rounded hover:bg-white/20 transition-all duration-200"
                                title="Hide panel"
                            >
                                <MdClose className="text-sm" />
                            </button>
                        </div>
                    </div>
                    {loading ? (
                        <div className="flex items-center justify-center py-6">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                        </div>
                    ) : error ? (
                        <div className="text-xs text-red-300 bg-red-500/20 px-3 py-2 rounded-lg border border-red-400/30">{error}</div>
                    ) : tab === "current" ? (
                        <div className="space-y-2">
                            <div className="flex items-center gap-1.5 text-sm font-bold opacity-90 mb-2">
                                <WiDaySunny className="text-xl text-yellow-300" />
                                Current Conditions
                            </div>
                            {currentData ? (
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-300/30 hover:scale-105 transition-transform duration-200">
                                        <div className="flex items-center gap-1 opacity-70 text-[10px] mb-0.5">
                                            <WiThermometer className="text-base" /> Temp
                                        </div>
                                        <div className="text-lg font-bold">{currentData.temperature_2m} °C</div>
                                    </div>
                                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-400/20 to-yellow-500/20 border border-orange-300/30 hover:scale-105 transition-transform duration-200">
                                        <div className="flex items-center gap-1 opacity-70 text-[10px] mb-0.5">
                                            <WiThermometer className="text-base" /> Feels Like
                                        </div>
                                        <div className="text-lg font-bold">{currentData.apparent_temperature} °C</div>
                                    </div>
                                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-300/30 hover:scale-105 transition-transform duration-200">
                                        <div className="flex items-center gap-1 opacity-70 text-[10px] mb-0.5">
                                            <WiHumidity className="text-base" /> Humidity
                                        </div>
                                        <div className="text-lg font-bold">{currentData.relative_humidity_2m} %</div>
                                    </div>
                                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400/20 to-indigo-500/20 border border-blue-300/30 hover:scale-105 transition-transform duration-200">
                                        <div className="flex items-center gap-1 opacity-70 text-[10px] mb-0.5">
                                            <WiRaindrop className="text-base" /> Precip
                                        </div>
                                        <div className="text-lg font-bold">{currentData.precipitation} mm</div>
                                    </div>
                                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-green-500/20 to-teal-500/20 border border-green-300/30 hover:scale-105 transition-transform duration-200">
                                        <div className="flex items-center gap-1 opacity-70 text-[10px] mb-0.5">
                                            <WiStrongWind className="text-base" /> Wind Speed
                                        </div>
                                        <div className="text-lg font-bold">{currentData.wind_speed_10m} km/h</div>
                                    </div>
                                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-green-400/20 to-emerald-500/20 border border-green-300/30 hover:scale-105 transition-transform duration-200">
                                        <div className="flex items-center gap-1 opacity-70 text-[10px] mb-0.5">
                                            <WiStrongWind className="text-base" /> Wind Gusts
                                        </div>
                                        <div className="text-lg font-bold">{currentData.wind_gusts_10m} km/h</div>
                                    </div>
                                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-300/30 hover:scale-105 transition-transform duration-200">
                                        <div className="flex items-center gap-1 opacity-70 text-[10px] mb-0.5">
                                            <WiStrongWind className="text-base" /> Wind Dir
                                        </div>
                                        <div className="text-lg font-bold">{currentData.wind_direction_10m}°</div>
                                    </div>
                                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 border border-indigo-300/30 hover:scale-105 transition-transform duration-200">
                                        <div className="flex items-center gap-1 opacity-70 text-[10px] mb-0.5">
                                            <WiCloudy className="text-base" /> Weather Code
                                        </div>
                                        <div className="text-lg font-bold">{currentData.weather_code ?? "-"}</div>
                                    </div>
                                </div>
                            ) : null}
                            {forecastData && (
                                <div className="mt-2 space-y-2">
                                    <div className="text-[10px] font-semibold opacity-80">Location Details</div>
                                    <div className="grid grid-cols-2 gap-2 text-[10px]">
                                        {forecastData.elevation && (
                                            <div className="p-2 rounded-lg bg-black/20 border border-white/10">
                                                <div className="opacity-70">Elevation</div>
                                                <div className="font-bold">{forecastData.elevation} m</div>
                                            </div>
                                        )}
                                        {forecastData.timezone && (
                                            <div className="p-2 rounded-lg bg-black/20 border border-white/10">
                                                <div className="opacity-70">Timezone</div>
                                                <div className="font-bold text-[9px]">{forecastData.timezone_abbreviation || forecastData.timezone}</div>
                                            </div>
                                        )}
                                        {forecastData.latitude && (
                                            <div className="p-2 rounded-lg bg-black/20 border border-white/10">
                                                <div className="opacity-70">Latitude</div>
                                                <div className="font-bold">{forecastData.latitude.toFixed(4)}°</div>
                                            </div>
                                        )}
                                        {forecastData.longitude && (
                                            <div className="p-2 rounded-lg bg-black/20 border border-white/10">
                                                <div className="opacity-70">Longitude</div>
                                                <div className="font-bold">{forecastData.longitude.toFixed(4)}°</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {forecastData?.hourly && (
                                <div className="mt-2 space-y-2">
                                    <div className="text-[10px] font-semibold opacity-80">24-Hour Forecast</div>
                                    <div className="max-h-[200px] overflow-y-auto space-y-1 pr-1">
                                        {(forecastData.hourly.time || []).slice(0, 24).map((t, i) => (
                                            <div
                                                key={t}
                                                className="p-1.5 rounded-lg bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-white/10 text-[10px]"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="font-medium">{formatTime(t, use24h)}</div>
                                                    <div className="flex items-center gap-2 opacity-90">
                                                        <span>{forecastData.hourly.temperature_2m?.[i]}°C</span>
                                                        <span className="text-[9px]">{forecastData.hourly.relative_humidity_2m?.[i]}%</span>
                                                        {forecastData.hourly.cloud_cover?.[i] !== undefined && (
                                                            <span className="text-[9px] opacity-75">☁️ {forecastData.hourly.cloud_cover[i]}%</span>
                                                        )}
                                                        {forecastData.hourly.precipitation?.[i] > 0 && (
                                                            <span className="text-blue-300">💧 {forecastData.hourly.precipitation[i]}mm</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {!currentData && (
                                <div className="text-xs opacity-80 text-center py-3">No current data available.</div>
                            )}
                        </div>
                    ) : tab === "forecast" ? (
                        <div className="space-y-2">
                            <div className="flex items-center gap-1.5 text-sm font-bold opacity-90 mb-2">
                                <WiCloudy className="text-xl text-blue-300" />
                                7-Day Forecast
                            </div>
                            {forecastData?.daily ? (
                                <div className="space-y-1.5">
                                    {(forecastData.daily.time || []).slice(0, 7).map((d, i) => {
                                        // Find hourly data for this day
                                        const dayStart = new Date(d).setHours(0, 0, 0, 0);
                                        const dayEnd = new Date(d).setHours(23, 59, 59, 999);
                                        const dayHourly = forecastData.hourly?.time?.map((ht, idx) => {
                                            const hTime = new Date(ht).getTime();
                                            if (hTime >= dayStart && hTime <= dayEnd) {
                                                return {
                                                    time: ht,
                                                    temp: forecastData.hourly.temperature_2m?.[idx],
                                                    humidity: forecastData.hourly.relative_humidity_2m?.[idx],
                                                    precip: forecastData.hourly.precipitation?.[idx],
                                                    cloud: forecastData.hourly.cloud_cover?.[idx],
                                                    wind: forecastData.hourly.wind_speed_10m?.[idx],
                                                };
                                            }
                                            return null;
                                        }).filter(Boolean).slice(0, 8); // Show 8 hours per day

                                        return (
                                            <div
                                                key={d}
                                                className="p-2 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-white/20 hover:scale-105 transition-transform duration-200"
                                            >
                                                <div className="flex items-center justify-between text-[11px] mb-1">
                                                    <div className="font-bold">{formatDate(d)}</div>
                                                    <div className="flex items-center gap-1.5 opacity-90">
                                                        <span className="text-blue-300">{forecastData.daily.temperature_2m_min?.[i]}°</span>
                                                        <span>/</span>
                                                        <span className="text-orange-300">{forecastData.daily.temperature_2m_max?.[i]}°</span>
                                                    </div>
                                                    <div className="flex items-center gap-0.5 opacity-90">
                                                        <WiRaindrop className="text-base text-blue-400" />
                                                        {forecastData.daily.precipitation_sum?.[i]} mm
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between text-[10px] mb-1 opacity-80">
                                                    <span>Wind: {forecastData.daily.wind_speed_10m_max?.[i]} km/h</span>
                                                    <span>Code: {forecastData.daily.weather_code?.[i] ?? "-"}</span>
                                                </div>
                                                {dayHourly && dayHourly.length > 0 && (
                                                    <div className="mt-1.5 pt-1.5 border-t border-white/10">
                                                        <div className="text-[9px] opacity-70 mb-1">Hourly Preview:</div>
                                                        <div className="flex flex-wrap gap-1">
                                                            {dayHourly.map((h, hIdx) => (
                                                                <div key={hIdx} className="text-[8px] px-1.5 py-0.5 rounded bg-white/10">
                                                                    {formatTime(h.time, use24h)}: {h.temp}° {h.cloud !== undefined && `☁️${h.cloud}%`}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-xs opacity-80 text-center py-3">No forecast data available.</div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <div className="flex items-center gap-1.5 text-sm font-bold opacity-90 mb-2">
                                <WiTime3 className="text-xl text-purple-300" />
                                Past 7 Days (Hourly)
                            </div>
                            {pastData?.hourly ? (
                                <>
                                    {pastData.elevation && (
                                        <div className="mb-2 p-2 rounded-lg bg-black/20 border border-white/10 text-[10px]">
                                            <div className="opacity-70">Location: {pastData.latitude?.toFixed(4)}°N, {pastData.longitude?.toFixed(4)}°E</div>
                                            <div className="opacity-70">Elevation: {pastData.elevation}m | {pastData.timezone_abbreviation || pastData.timezone}</div>
                                        </div>
                                    )}
                                    <div className="space-y-1.5">
                                        {(pastData.hourly.time || []).slice(-72).map((t, i) => (
                                            <div
                                                key={t}
                                                className="p-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-white/20 hover:scale-105 transition-transform duration-200"
                                            >
                                                <div className="flex items-center justify-between text-[11px]">
                                                    <div className="flex items-center gap-1 opacity-90 font-medium">
                                                        <FiClock className="text-xs" />
                                                        {formatDate(t)} {formatTime(t, use24h)}
                                                    </div>
                                                    <div className="opacity-90 font-semibold">
                                                        {pastData.hourly.temperature_2m?.[i]}°C
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between text-[10px] mt-1 opacity-80">
                                                    <span>RH: {pastData.hourly.relative_humidity_2m?.[i]}%</span>
                                                    <span className="flex items-center gap-0.5">
                                                        <WiRaindrop className="text-sm text-blue-400" />
                                                        {pastData.hourly.precipitation?.[i]} mm
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="text-xs opacity-80 text-center py-3">No past data available.</div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Location Sales Data Panel - below left panel */}
            {showLeftPanel && (
                <div className={`absolute top-[280px] left-1 p-2 rounded-2xl ${glass} text-white w-[90vw] md:w-[280px] max-h-[calc(70vh-3rem)] overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent z-[10000] transition-all duration-300 hover:shadow-3xl`}>
                    <div className="flex items-center justify-between mb-1.5 gap-2">
                        <div className="text-[11px] font-bold opacity-90">📊 Sales Analytics</div>
                    </div>
                    {locationSalesLoading ? (
                        <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        </div>
                    ) : locationSalesData ? (
                        <div className="space-y-1.5">
                            {/* Location Info - Compact */}
                            <div className="p-1.5 rounded-lg bg-black/30 border border-white/10 text-[9px]">
                                <div className="font-bold text-[10px] text-blue-200">{locationSalesData.location_provided}</div>
                                {locationSalesData.applied_filter && (
                                    <div className="opacity-70 mt-0.5 text-[8px]">
                                        {locationSalesData.applied_filter.level} • {locationSalesData.current_date}
                                    </div>
                                )}
                            </div>

                            {/* Combined 30 Days - Compact Table */}
                            {locationSalesData.combined_30_days?.products && locationSalesData.combined_30_days.products.length > 0 && (
                                <div>
                                    <div className="text-[9px] font-bold opacity-80 mb-1 px-1 flex items-center gap-1">
                                        <span className="text-green-300">●</span> Last 30 Days ({locationSalesData.combined_30_days.products.length})
                                    </div>
                                    <div className="space-y-0.5 max-h-[120px] overflow-y-auto">
                                        {locationSalesData.combined_30_days.products.map((product, idx) => (
                                            <div key={idx} className="flex items-center justify-between px-1.5 py-0.5 rounded bg-gradient-to-r from-green-500/15 to-emerald-500/10 hover:from-green-500/25 hover:to-emerald-500/20 transition-all text-[9px]">
                                                <span className="truncate flex-1 font-medium">{product.aka_name}</span>
                                                <span className="font-bold text-green-300 ml-2 tabular-nums">{product.total_sold}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Sales History - Collapsible Months */}
                            {locationSalesData.two_years && (
                                <div>
                                    <div className="text-[9px] font-bold opacity-80 mb-1 px-1 flex items-center gap-1">
                                        <span className="text-purple-300">●</span> Sales History
                                    </div>
                                    <div className="space-y-0.5">
                                        {Object.keys(locationSalesData.two_years).reverse().map((year) => (
                                            locationSalesData.two_years[year]?.map((monthData, idx) => {
                                                if (!monthData.products || monthData.products.length === 0) return null;
                                                const monthKey = `${year}-${idx}`;
                                                const isExpanded = expandedMonths[monthKey];
                                                const totalSold = monthData.products.reduce((sum, p) => sum + p.total_sold, 0);

                                                return (
                                                    <div key={monthKey}>
                                                        <button
                                                            onClick={() => setExpandedMonths(prev => ({ ...prev, [monthKey]: !prev[monthKey] }))}
                                                            className="w-full flex items-center justify-between px-1.5 py-1 rounded-lg bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-white/10 hover:from-blue-500/30 hover:to-purple-500/30 transition-all text-[9px]"
                                                        >
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-[8px]">{isExpanded ? '▼' : '▶'}</span>
                                                                <span className="font-semibold">{monthData.month} {year}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[8px] opacity-70">{monthData.products.length} items</span>
                                                                <span className="font-bold text-purple-300 tabular-nums">{totalSold}</span>
                                                            </div>
                                                        </button>
                                                        {isExpanded && (
                                                            <div className="mt-0.5 ml-2 space-y-0.5 border-l border-white/10 pl-1.5">
                                                                {monthData.products.map((product, pIdx) => (
                                                                    <div key={pIdx} className="flex items-center justify-between text-[8px] opacity-90 py-0.5">
                                                                        <span className="truncate flex-1">{product.aka_name}</span>
                                                                        <span className="font-bold ml-2 tabular-nums text-blue-200">{product.total_sold}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ).filter(Boolean))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-[10px] opacity-70 text-center py-3">Click on map to see sales data</div>
                    )}
                </div>
            )}

            <ClockBar
                glass={glass}
                formattedTime={formattedTime}
                formattedDate={formattedDate}
                use24h={use24h}
                setUse24h={setUse24h}
            />

            <LocationButton
                glass={glass}
                isLocating={isLocating}
                onClick={handleGetCurrentLocation}
            />
            {/* City loading / error indicator */}
            {(cityLoading || cityError) && (
                <div className="absolute top-1 left-1/2 -translate-x-1/2 z-[10003]">
                    <div className={`px-2 py-1 rounded-lg ${glass} text-white text-[10px] flex items-center gap-2`}>
                        {cityLoading && (
                            <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                <span>Loading nearby cities…</span>
                            </>
                        )}
                        {!cityLoading && cityError && (
                            <>
                                <span className="text-red-200">{cityError}</span>
                                <button
                                    onClick={() => updateVisibleCities()}
                                    className="px-2 py-0.5 rounded border border-white/40 bg-white/10 hover:bg-white/20"
                                >
                                    Retry
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Weather;


